import {createSelector} from '@reduxjs/toolkit'

import {eq, minZero} from '@shared/helpers/lang'

import {ChargePointPolicy, PreferredPoint} from '@web/core/constants'
import {
    isPointChargingAvailable,
    getUsingRechargePointAmount,
    getPointAmountByPreferredPointPolicy,
} from '@web/helpers/policies'
import {cardEasyPayCommissionSelector, cardPromotionInfoSelector} from '@web/slices/card/slice'
import {chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {orderAmountInfoSelector} from '@web/slices/orderTotal/slice'
import {payLaterSelector} from '@web/slices/payLater/slice'
import {payMethodInfoSelector} from '@web/slices/paymentMethod/slice'
import {naverPayPointSelector} from '@web/slices/pointPayment/slice'
import {rewardPointSelector} from '@web/slices/rewardPoint/slice'

/** 결제수단으로 결제하는 금액 정보 selector */
export const paymentAmountInfoSelectors = createSelector(
    orderAmountInfoSelector,
    payMoneyInfoSelector,
    naverPayPointSelector,
    rewardPointSelector,
    chargePointSelector,
    payLaterSelector,
    payMethodInfoSelector,
    cardPromotionInfoSelector,
    cardEasyPayCommissionSelector,
    (
        {orderAmount: amount},
        {isPayMoneyUser, chargePointPolicy, isUsedLikePayMoney},
        naverPayPoint,
        rewardPoint,
        chargePoint,
        payLater,
        {selectedRechargePayMethod, selectedEasyCardPayMethod},
        {selected: selectedPromotionGroup, shouldPromotionInfoSkip},
        easyCardCommission,
    ) => {
        /** 충전포인트 페이머니 취급여부 */
        const isPayMoneyPolicy = eq(chargePointPolicy, ChargePointPolicy.PAYMONEY)
        /** 최종 주문 금액 */
        const orderAmount = selectedEasyCardPayMethod ? amount + easyCardCommission : amount
        /** 남은 금액이 없다면, 충전결제 미선택 */
        const pointRechargingEnabled =
            selectedRechargePayMethod && orderAmount - naverPayPoint.usingAmount - payLater.usingAmount > 0
        const {
            // 사용되는 페이포인트
            usingRewardPointAmount,
            // 사용되는 페이머니
            usingChargePointAmount,
            // 사용되는 현금성포인트 (적립포인트 내 분류)
            usingCashRewardPointAmount,
        } = getPointAmountByPreferredPointPolicy({
            preferredPointType: isPayMoneyPolicy ? PreferredPoint.REWARD_POINT : naverPayPoint.preferredInfo.type,
            payMoneyStateAvailable: isPayMoneyUser,
            usingPointAmountTotal: naverPayPoint.usingAmount,
            paymentAmount: orderAmount,
            usingPayLaterAmount: payLater.usingAmount,
            chargePointAmount: chargePoint.amount,
            rewardPointAmount: minZero(rewardPoint.amount),
            cashRewardPointAmount: minZero(rewardPoint.cashPoint),
            pointRechargingEnabled,
            pointChargingAccountExists: isPointChargingAvailable({
                payMoneyUserStatus: chargePoint.payMoney.userStatus,
            }),
            isPayMoneyPolicy,
        })
        // 충전후 사용되는 페이머니
        const usingRechargePointAmount = getUsingRechargePointAmount({
            paymentAmount: orderAmount,
            usingRewardPointAmount,
            usingChargePointAmount,
            usingPayLaterAmount: payLater.usingAmount,
            rechargePointAmount: chargePoint.recharging.amount,
            payMoneyStateAvailable: isPayMoneyUser,
        })
        const usingPayLaterPointAmount = payLater.usingAmount
        const cardPromotionDiscountAmount =
            !shouldPromotionInfoSkip && selectedPromotionGroup?.totalPromotionApplyAmount
                ? selectedPromotionGroup.totalPromotionApplyAmount
                : 0
        const totalPaymentAmount = isUsedLikePayMoney
            ? orderAmount - usingRewardPointAmount - usingPayLaterPointAmount - cardPromotionDiscountAmount
            : orderAmount -
              usingRewardPointAmount -
              usingChargePointAmount -
              usingPayLaterPointAmount -
              cardPromotionDiscountAmount
        const rechargePaymentAmount = Math.min(
            isUsedLikePayMoney ? totalPaymentAmount - usingChargePointAmount : totalPaymentAmount,
            chargePoint.reloadable.amount,
        )
        const usingPayMethodAmount = isUsedLikePayMoney
            ? totalPaymentAmount - usingChargePointAmount - usingRechargePointAmount
            : totalPaymentAmount - usingRechargePointAmount
        const totalDiscountAmount = orderAmount - cardPromotionDiscountAmount

        return {
            /** 사용되는 페이포인트 (적립포인트) */
            usingRewardPointAmount,
            /** 사용되는 현금성 페이포인트 (적립포인트 내 분류) */
            usingCashRewardPointAmount,
            /** 사용되는 페이머니 (충전포인트) */
            usingChargePointAmount,
            /** 사용되는 후불결제 포인트 */
            usingPayLaterPointAmount,
            /** 충전후 사용되는 페이머니 */
            usingRechargePointAmount,
            /** 사용되는 전체 페이머니 */
            totalUsingChargePoint: usingChargePointAmount + usingRechargePointAmount,
            /** 결제수단 금액 */
            paymentAmount: totalPaymentAmount,
            /** 결제금액 */
            payAmount: totalPaymentAmount + usingPayLaterPointAmount,
            /** 충전결제가 처리해야하는 금액 */
            rechargePaymentAmount,
            /** 결제수단으로 결제하는 결제 금액 */
            usingPayMethodAmount,
            /** 모든 할인수단 적용된 결제 금액 (보조 결제수단 포함X) */
            totalDiscountAmount,
        }
    },
)

export const fullAmountPointPaymentSelector = createSelector(
    orderAmountInfoSelector,
    naverPayPointSelector,
    ({orderAmount}, {usingAmount: pointUsingAmount}) => {
        return {
            isFullAmountPointPayment: pointUsingAmount > 0 && orderAmount <= pointUsingAmount,
        }
    },
)
